import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Modal, Button, IconSpinner } from '../../components';
import {
  parseCSVFile,
  validateCSVData,
  generateCSVTemplate,
} from './BulkUploadListingsModal.helpers';
import { buildListingTitle } from '../../util/fieldHelpers';
import css from './BulkUploadListingsModal.module.css';

const STEP_INSTRUCTIONS = 'instructions';
const STEP_UPLOAD = 'upload';
const STEP_PREVIEW = 'preview';
const STEP_PROGRESS = 'progress';
const STEP_RESULT = 'result';

const ProgressBar = ({ current, total }) => {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className={css.progressBarWrap}>
      <div className={css.progressBar} style={{ width: `${pct}%` }} />
      <span className={css.progressLabel}>{pct}%</span>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const intl = useIntl();
  const cls = classNames(css.badge, {
    [css.badgeSuccess]: status === 'success',
    [css.badgeError]: status === 'error',
    [css.badgePending]: status === 'pending',
  });
  const label = intl.formatMessage({ id: `BulkUploadListingsModal.status.${status}` });
  return <span className={cls}>{label}</span>;
};

// CSV_FIELDS: translation key suffixes for the field reference table.
// field      → used to build translation keys for column name, description, example
// fieldKey   → unique key for React list rendering
const CSV_FIELD_KEYS = [
  'category',
  'make',
  'model',
  'yearModel',
  'hours',
  'location',
  'description',
  'price',
];

const BulkUploadListingsModal = ({ isOpen, onClose, onUpload, uploadResults, isUploading, onManageDisableScrolling }) => {
  const intl = useIntl();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(STEP_INSTRUCTIONS);
  const [csvData, setCsvData] = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const handleClose = () => {
    setStep(STEP_INSTRUCTIONS);
    setCsvData(null);
    setParseErrors([]);
    onClose();
  };

  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'listings-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFile = async file => {
    if (!file || !file.name.endsWith('.csv')) {
      setParseErrors([{
        row: 0,
        message: intl.formatMessage({ id: 'BulkUploadListingsModal.error.invalidFileType' }),
      }]);
      return;
    }
    try {
      const parsed = await parseCSVFile(file);
      const errors = validateCSVData(parsed, intl);
      if (errors.length > 0) {
        setParseErrors(errors);
        setCsvData(null);
      } else {
        setParseErrors([]);
        setCsvData(parsed);
        setStep(STEP_PREVIEW);
      }
    } catch (e) {
      setParseErrors([{ row: 0, message: e.message }]);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = () => {
    setStep(STEP_PROGRESS);
    onUpload(csvData);
  };

  // Move to result step when upload finishes
  React.useEffect(() => {
    if (uploadResults && step === STEP_PROGRESS && !isUploading) {
      setStep(STEP_RESULT);
    }
  }, [uploadResults, isUploading]);

  const successCount = uploadResults ? uploadResults.filter(r => r.status === 'success').length : 0;
  const errorCount   = uploadResults ? uploadResults.filter(r => r.status === 'error').length   : 0;

  return (
    <Modal
      id="BulkUploadListingsModal"
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      contentClassName={css.modalContent}
      containerClassName={css.bulkUploadModal}
    >
      <div className={css.root}>

        {/* ── STEP: Instructions ─────────────────────────────── */}
        {step === STEP_INSTRUCTIONS && (
          <div className={css.stepWrap}>
            <h2 className={css.title}>
              <FormattedMessage id="BulkUploadListingsModal.instructions.title" />
            </h2>
            <p className={css.subtitle}>
              <FormattedMessage id="BulkUploadListingsModal.instructions.subtitle" />
            </p>

            <ol className={css.instructionList}>
              {[1, 2, 3, 4].map(n => (
                <li key={n}>
                  <strong>
                    <FormattedMessage id={`BulkUploadListingsModal.instructions.step${n}Title`} />
                  </strong>
                  <p>
                    <FormattedMessage id={`BulkUploadListingsModal.instructions.step${n}Desc`} />
                  </p>
                </li>
              ))}
            </ol>

            <div className={css.fieldGuide}>
              <h3 className={css.fieldGuideTitle}>
                <FormattedMessage id="BulkUploadListingsModal.fieldGuide.title" />
              </h3>
              <table className={css.fieldTable}>
                <thead>
                  <tr>
                    <th><FormattedMessage id="BulkUploadListingsModal.fieldGuide.colColumn" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.fieldGuide.colRequired" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.fieldGuide.colDescription" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.fieldGuide.colExample" /></th>
                  </tr>
                </thead>
                <tbody>
                  {CSV_FIELD_KEYS.map(key => (
                    <tr key={key}>
                      <td>
                        <code>
                          <FormattedMessage id={`BulkUploadListingsModal.fieldGuide.field.${key}.name`} />
                        </code>
                      </td>
                      <td className={css.reqYes}>
                        <FormattedMessage id="BulkUploadListingsModal.fieldGuide.required" />
                      </td>
                      <td>
                        <FormattedMessage id={`BulkUploadListingsModal.fieldGuide.field.${key}.desc`} />
                      </td>
                      <td>
                        <code>
                          <FormattedMessage id={`BulkUploadListingsModal.fieldGuide.field.${key}.example`} />
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className={css.fixedNote}>
                <FormattedMessage id="BulkUploadListingsModal.fieldGuide.fixedNote" />
              </p>
            </div>

            <div className={css.actions}>
              <Button className={css.btnSecondary} onClick={handleDownloadTemplate}>
                <FormattedMessage id="BulkUploadListingsModal.btn.downloadTemplate" />
              </Button>
              <Button className={css.btnPrimary} onClick={() => setStep(STEP_UPLOAD)}>
                <FormattedMessage id="BulkUploadListingsModal.btn.continueToUpload" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP: Upload ───────────────────────────────────── */}
        {step === STEP_UPLOAD && (
          <div className={css.stepWrap}>
            <h2 className={css.title}>
              <FormattedMessage id="BulkUploadListingsModal.upload.title" />
            </h2>
            <p className={css.subtitle}>
              <FormattedMessage id="BulkUploadListingsModal.upload.subtitle" />
            </p>

            <div
              className={classNames(css.dropzone, { [css.dropzoneActive]: dragOver })}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <div className={css.dropzoneIcon}>📄</div>
              <p className={css.dropzoneText}>
                <FormattedMessage id="BulkUploadListingsModal.upload.dropzoneText" />
              </p>
              <p className={css.dropzoneHint}>
                <FormattedMessage id="BulkUploadListingsModal.upload.dropzoneHint" />
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className={css.hiddenInput}
                onChange={e => handleFile(e.target.files[0])}
              />
            </div>

            {parseErrors.length > 0 && (
              <div className={css.errorBox}>
                <strong>
                  <FormattedMessage id="BulkUploadListingsModal.upload.errorsFound" />
                </strong>
                <ul>
                  {parseErrors.map((err, i) => (
                    <li key={i}>
                      {err.row > 0
                        ? intl.formatMessage(
                            { id: 'BulkUploadListingsModal.upload.errorRow' },
                            { row: err.row, message: err.message }
                          )
                        : err.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={css.actions}>
              <Button className={css.btnSecondary} onClick={() => setStep(STEP_INSTRUCTIONS)}>
                <FormattedMessage id="BulkUploadListingsModal.btn.back" />
              </Button>
              <Button className={css.btnSecondary} onClick={handleDownloadTemplate}>
                <FormattedMessage id="BulkUploadListingsModal.btn.downloadTemplate" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP: Preview ──────────────────────────────────── */}
        {step === STEP_PREVIEW && csvData && (
          <div className={css.stepWrap}>
            <h2 className={css.title}>
              <FormattedMessage
                id="BulkUploadListingsModal.preview.title"
                values={{ count: csvData.length }}
              />
            </h2>
            <p className={css.subtitle}>
              <FormattedMessage id="BulkUploadListingsModal.preview.subtitle" />
            </p>

            <div className={css.previewTableWrap}>
              <table className={css.previewTable}>
                <thead>
                  <tr>
                    <th><FormattedMessage id="BulkUploadListingsModal.preview.colNum" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.preview.colTitle" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.preview.colPrice" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.preview.colLocation" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.preview.colYear" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.preview.colHours" /></th>
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((row, i) => (
                    <tr key={i}>
                      <td className={css.rowNum}>{i + 1}</td>
                      <td className={css.cellTitle}>{buildListingTitle(row)}</td>
                      <td>${(parseInt(row.price, 10) / 100).toFixed(2)}</td>
                      <td>{row.loc}</td>
                      <td>{row.year}</td>
                      <td>{row.hours}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={css.actions}>
              <Button className={css.btnSecondary} onClick={() => setStep(STEP_UPLOAD)}>
                <FormattedMessage id="BulkUploadListingsModal.btn.back" />
              </Button>
              <Button className={css.btnPrimary} onClick={handleUpload}>
                <FormattedMessage
                  id="BulkUploadListingsModal.btn.submit"
                  values={{ count: csvData.length }}
                />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP: Progress ─────────────────────────────────── */}
        {step === STEP_PROGRESS && (
          <div className={css.stepWrap}>
            <h2 className={css.title}>
              <FormattedMessage id="BulkUploadListingsModal.progress.title" />
            </h2>
            <p className={css.subtitle}>
              <FormattedMessage id="BulkUploadListingsModal.progress.subtitle" />
            </p>
            <div className={css.spinnerWrap}>
              <IconSpinner />
            </div>
            {uploadResults && csvData && (
              <>
                <ProgressBar current={uploadResults.length} total={csvData.length} />
                <p className={css.progressCount}>
                  <FormattedMessage
                    id="BulkUploadListingsModal.progress.count"
                    values={{ current: uploadResults.length, total: csvData.length }}
                  />
                </p>
              </>
            )}
          </div>
        )}

        {/* ── STEP: Result ───────────────────────────────────── */}
        {step === STEP_RESULT && uploadResults && (
          <div className={css.stepWrap}>
            <h2 className={css.title}>
              <FormattedMessage id="BulkUploadListingsModal.result.title" />
            </h2>

            <div className={css.resultSummary}>
              <div className={css.resultStat}>
                <span className={classNames(css.resultStatNum, css.statSuccess)}>{successCount}</span>
                <span className={css.resultStatLabel}>
                  <FormattedMessage id="BulkUploadListingsModal.result.created" />
                </span>
              </div>
              <div className={css.resultStat}>
                <span className={classNames(css.resultStatNum, css.statError)}>{errorCount}</span>
                <span className={css.resultStatLabel}>
                  <FormattedMessage id="BulkUploadListingsModal.result.failed" />
                </span>
              </div>
            </div>

            <div className={css.previewTableWrap}>
              <table className={css.previewTable}>
                <thead>
                  <tr>
                    <th><FormattedMessage id="BulkUploadListingsModal.preview.colNum" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.result.colTitle" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.result.colStatus" /></th>
                    <th><FormattedMessage id="BulkUploadListingsModal.result.colDetails" /></th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResults.map((r, i) => (
                    <tr key={i}>
                      <td className={css.rowNum}>{i + 1}</td>
                      <td className={css.cellTitle}>{r.title}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className={css.resultDetail}>
                        {r.status === 'success' ? (
                          <a href={`/l/${r.listingId}`} target="_blank" rel="noopener noreferrer">
                            <FormattedMessage id="BulkUploadListingsModal.result.viewDraft" />
                          </a>
                        ) : (
                          r.error
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={css.actions}>
              <Button className={css.btnPrimary} onClick={handleClose}>
                <FormattedMessage id="BulkUploadListingsModal.btn.done" />
              </Button>
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
};

export default BulkUploadListingsModal;
